import React from 'react';
import {
    Modal,
    Box,
    Typography,
    Paper,
    Grid,
    Chip,
    Divider,
    IconButton,
    Stack,
    Card,
    CardContent,
    Button,
} from '@mui/material';
import {
    Close as CloseIcon,
    LocationOn,
    Business,
    AccessTime,
    Person,
    CalendarToday,
    Info,
    Description,
    PinDrop,
    Work,
} from '@mui/icons-material';
import { format } from 'date-fns';

const JobModal = ({ open =true, onClose, jobData }: any) => {
    // Format date function
    const formatDate = (dateString: any) => {
        if (!dateString) return 'N/A';
        try {
            return format(new Date(dateString), 'dd MMM yyyy');
        } catch {
            return dateString;
        }
    };

    const formatTime = (dateString: any) => {
        if (!dateString) return 'N/A';
        try {
            return format(new Date(dateString), 'hh:mm a');
        } catch {
            return dateString;
        }
    };

    const formatDateTime = (dateString: any) => {
        if (!dateString) return 'N/A';
        try {
            return format(new Date(dateString), 'dd MMM yyyy, hh:mm a');
        } catch {
            return dateString;
        }
    };

    const getStatusColor = (status: any) => {
        switch (status) {
            case 0:
                return 'warning';
            case 1:
                return 'success';
            case 2:
                return 'error';
            case 3:
                return 'success';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: any) => {
        console.log(status)
        switch (status) {
            case 0:
                return 'Pending';
            case 1:
                return 'Sent';
            case 2:
                return 'Canceled';
            case 3:
                return 'Invoiced';
            default:
                return 'Unknown';
        }
    };

    const getShiftLabel = (type: any) => {
        switch (type) {
            case 1:
                return 'Day Shift';
            case 2:
                return 'Night Shift';
            case 3:
                return 'Rotating';
            default:
                return 'Standard';
        }
    };

    const data = jobData;

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="job-details-modal"
            aria-describedby="job-details-description"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            <Paper
                sx={{
                    maxWidth: 900,
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    borderRadius: 3,
                    position: 'relative',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                }}
            >
                <Box
                    sx={{
                        p: 3,
                        borderTopLeftRadius: 12,
                        borderTopRightRadius: 12,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                    }}
                >
                    <Box>
                        <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                            Job Details
                        </Typography>
                        <Typography variant="h5" component="h2" fontWeight={600}>
                            {data?.whatsAppName}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                            <Chip
                                label={getStatusLabel(data?.jobStatus)}
                                color={getStatusColor(data?.jobStatus)}
                                size="small"
                                sx={{ fontWeight: 500 }}
                            />
                            <Chip
                                label={`Week ${data?.week}`}
                                variant="outlined"
                                size="small"
                            />
                            <Chip
                                label={data?.region}
                                variant="outlined"
                                size="small"
                            />
                        </Stack>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: 'inherit',
                            bgcolor: 'rgba(255,255,255,0.1)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                <CardContent>
                                    <Stack direction="row" spacing={3} flexWrap="wrap">
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Business fontSize="small" color="info" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Company
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                   {data?.projectName} - {data?.companyName}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <LocationOn fontSize="small" color="info" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Location
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {data?.location}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <PinDrop fontSize="small" color="info" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Post Code
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {data?.postCode}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>

                            <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CalendarToday fontSize="small" color="info" />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Job Date
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {formatDate(data?.jobDate)}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <AccessTime fontSize="small" color="info" />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Shift
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {formatTime(data?.startTime)} - {formatTime(data?.endTime)}
                                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                            ({getShiftLabel(data?.shiftType)})
                                                        </Typography>
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Work fontSize="small" color="info" />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Hours
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {data?.hours} hrs
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Person fontSize="small" color="info" />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Required Workers
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {data?.requiredWorkers}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                <CardContent>
                                    <Stack direction="row" spacing={1} alignItems="flex-start">
                                        <Description fontSize="small" color="info" sx={{ mt: 0.5 }} />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Description
                                            </Typography>
                                            <Typography variant="body2">
                                                {data?.jobDescription}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>

                            {data?.additionalInformation && (
                                <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, borderColor: 'warning.main' }}>
                                    <CardContent>
                                        <Stack direction="row" spacing={1} alignItems="flex-start">
                                            <Info fontSize="small" color="warning" sx={{ mt: 0.5 }} />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Additional Information
                                                </Typography>
                                                <Typography variant="body2" color="warning.dark">
                                                    {data?.additionalInformation}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            )}
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                                <CardContent>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                        Additional Details
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />

                                    {data?.onCallNumber && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                On Call
                                            </Typography>
                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                <Typography variant="body2" fontWeight={500}>
                                                    {data?.onCallNumber}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    )}

                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Job Number
                                        </Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {data?.jobNumber || 'N/A'}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Created
                                        </Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {formatDateTime(data?.jobDate)}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Hotel Status {" "}
                                        </Typography>
                                        <Chip
                                            label={data?.hotelStatus === 1 ? 'Booked' : 'Not Booked'}
                                            size="small"
                                            color={data?.hotelStatus === 1 ? 'primary' : 'default'}
                                        />
                                    </Box>

                                    {(data?.notes || data?.notesJson) && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Notes
                                            </Typography>
                                            <Typography variant="body2">
                                                {data?.notes || data?.notesJson || 'N/A'}
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" style={{boxShadow: "none"}} onClick={onClose}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Modal>
    );
};

export default JobModal;
